/** Marketing i18n fragment — intgArgo (integrations/argo). */

export const intgArgoEn = {
  meta: {
    title: "Argo integration — deployment gates with human-approved promotion",
    description:
      "Where Argo is configured — a per-tenant opt-in — FixControl reads rollout status, opens a deployment approval gate, and promotes a paused rollout only after a person approves. Promotion is dispatched through a retried outbox and recorded in the decision ledger.",
  },
  hero: {
    eyebrow: "Argo integration",
    title: "A paused rollout moves",
    highlight: "when someone on your team approves",
    subtitle:
      "Where Argo is configured (your organization opts in), FixControl reads rollout status and opens a deployment gate. The rollout stays paused until someone on your team approves the gate; only then is the promotion dispatched.",
    primaryCta: "Book a demo",
    secondaryCta: "See deployment governance",
    bullet1: "Your organization opts in",
    bullet2: "Approval before promotion",
    bullet3: "Health per capability",
  },
  what: {
    eyebrow: "What it does",
    title: "How a gated promotion works",
  },
  features: {
    status: {
      title: "Rollout status",
      body: "Where Argo is configured, FixControl reads rollout and deployment status, scoped to your tenant. The gate is opened against the state Argo reports.",
    },
    gate: {
      title: "Deployment approval gate",
      body: "A deployment approval gate makes promotion an explicit, attributed decision. Until the gate is decided, the rollout stays paused.",
    },
    promotion: {
      title: "Human-approved promotion",
      body: "Approving the gate is what promotes the paused rollout. There is no promotion path that skips the gate.",
    },
    rollback: {
      title: "Abort & rollback visibility",
      body: "Where Argo reports abort and rollback state, FixControl surfaces it during the gate. The approver sees what the rollout is doing before deciding.",
    },
    scoped: {
      title: "Scoped & gated",
      body: "Promotion runs only through the gate and only under the Argo access you configured. Argo governance is opt-in per tenant; nothing is gated or promoted unless you set it up.",
    },
    audit: {
      title: "Evidence & audit",
      body: "The promotion decision is recorded with the approver, their role, and the evidence they saw. Rollout history and the decision ledger match.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Status read and promotion are separate capabilities",
    description:
      "Argo may be reachable for status while promotion isn’t configured, or the reverse. FixControl tracks the health of each path on its own, so the gate reflects what is wired up.",
    readStatus: {
      name: "Read rollout / deployment status",
      body: "Available where Argo is configured and reachable. Status reads keep the deployment gate tied to the rollout’s live state.",
    },
    openGate: {
      name: "Open deployment approval gate",
      body: "FixControl opens the gate as a recorded, attributed step. The promotion decision that follows is always a person’s.",
    },
    promote: {
      name: "Promote a paused rollout",
      body: "Runs where Argo is configured, and only after the gate is approved. The promote call is dispatched through the integration outbox.",
    },
    rollback: {
      name: "Abort / rollback visibility",
      body: "Shown where Argo is configured to report it, so abort and rollback state is visible while the gate is pending.",
    },
    autonomous: {
      name: "Promotion without an approval",
      body: "Doesn’t exist here. A paused rollout is promoted by an approved gate decision or not at all.",
    },
    singleGreen: {
      name: "A blanket ‘connected’ state",
      body: "Not used. Status read and promotion report health separately, so a broken promote path can’t hide behind a working status read.",
    },
  },
  failures: {
    eyebrow: "If promotion fails",
    title: "The gate holds until someone decides",
    noteTitle: "What happens after approval",
    noteBody:
      "Once the gate is approved, the promote action dispatches through the integration outbox — retried on transient errors, dead-lettered and flagged on the operational timeline when it keeps failing. The gate decision itself is written to the decision ledger with actor, role, channel, evidence, and consequence.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Deployment gate questions",
    q1: {
      q: "Does FixControl deploy to production on its own?",
      a: "No. Promotion happens only through an approved deployment gate. Until a person approves, the rollout stays paused.",
    },
    q2: {
      q: "What does FixControl need to promote a rollout?",
      a: "Argo configured and reachable for the tenant — an explicit opt-in — plus an approved gate. Without the approval, the gate stays pending and the rollout stays paused.",
    },
    q3: {
      q: "What happens if a promotion fails?",
      a: "The promote call retries from the outbox. If it keeps failing, the job dead-letters and the timeline records a failed promotion.",
    },
    q4: {
      q: "Can FixControl roll back automatically?",
      a: "No. FixControl surfaces abort and rollback state where Argo reports it, but rollback is your team’s decision. There is no automated rollback.",
    },
    q5: {
      q: "Are deployment promotions audited?",
      a: "Yes. Each gate decision is in the decision ledger, and the promotion it authorized is on the rollout history. The two line up.",
    },
    q6: {
      q: "Does our Argo or Kubernetes need to be publicly reachable?",
      a: "No. Where your cluster is private, the connection runs through the FC Agent: a service in your own environment that connects outbound to FixControl and executes the approved promotion inside your network. Nothing needs to be exposed.",
    },
  },
  related: {
    governance: {
      label: "Deployment governance",
      description: "Evidence in, one recorded decision, one promotion.",
    },
    ledger: {
      label: "Approval ledger",
      description: "Where every promotion decision is stored.",
    },
    agent: {
      label: "FC Agent — private infrastructure",
      description: "Outbound-only connectivity for private clusters and CI.",
    },
  },
  cta: {
    title: "Gate your next promotion",
    description:
      "Book a demo and approve a paused rollout yourself — where Argo is configured, promotion waits for exactly that.",
    primary: "Book a demo",
    secondary: "Ask FixControl",
  },
};

export const intgArgoNl = {
  meta: {
    title: "Argo-integratie — deployment-poorten met door een mens goedgekeurde promotie",
    description:
      "Waar Argo geconfigureerd is — een opt-in per tenant — leest FixControl rollout-status, opent een deployment-goedkeuringspoort en promoot een gepauzeerde rollout pas nadat een mens goedkeurt. De promotie wordt via een herprobeerde outbox verstuurd en vastgelegd in het beslis-grootboek.",
  },
  hero: {
    eyebrow: "Argo-integratie",
    title: "Een gepauzeerde rollout gaat verder",
    highlight: "als iemand van je team goedkeurt",
    subtitle:
      "Waar Argo geconfigureerd is (je organisatie zet het zelf aan), leest FixControl rollout-status en opent een deployment-poort. De rollout blijft gepauzeerd tot iemand van je team de poort goedkeurt; pas dan wordt de promotie verstuurd.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk deployment-governance",
    bullet1: "Je organisatie zet het zelf aan",
    bullet2: "Goedkeuring vóór promotie",
    bullet3: "Health per capability",
  },
  what: {
    eyebrow: "Wat het doet",
    title: "Hoe een gegate promotie werkt",
  },
  features: {
    status: {
      title: "Rollout-status",
      body: "Waar Argo geconfigureerd is, leest FixControl rollout- en deploymentstatus, gescoped op je tenant. De poort wordt geopend tegen de staat die Argo rapporteert.",
    },
    gate: {
      title: "Deployment-poort voor goedkeuring",
      body: "Een deployment-goedkeuringspoort maakt van promotie een expliciete, toewijsbare beslissing. Totdat de poort beslist is, blijft de rollout gepauzeerd.",
    },
    promotion: {
      title: "Door een mens goedgekeurde promotie",
      body: "De poort goedkeuren is wat de gepauzeerde rollout promoot. Er is geen promotiepad dat de poort overslaat.",
    },
    rollback: {
      title: "Abort- en rollback-zichtbaarheid",
      body: "Waar Argo abort- en rollback-status rapporteert, brengt FixControl die naar voren tijdens de poort. De goedkeurder ziet wat de rollout doet voordat hij beslist.",
    },
    scoped: {
      title: "Gescoped & gegated",
      body: "Promotie loopt alleen via de poort en alleen onder de Argo-toegang die je hebt geconfigureerd. Argo-governance is opt-in per tenant; er wordt niets gegate of gepromoot tenzij je het inricht.",
    },
    audit: {
      title: "Bewijs & audit",
      body: "De promotiebeslissing wordt vastgelegd met de goedkeurder, zijn rol en het bewijs dat hij zag. De rollout-historie en het beslis-grootboek komen overeen.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Status lezen en promoten zijn aparte capabilities",
    description:
      "Argo kan bereikbaar zijn voor status terwijl promotie niet is geconfigureerd, of andersom. FixControl houdt de health van elk pad apart bij, zodat de poort weerspiegelt wat er is aangesloten.",
    readStatus: {
      name: "Rollout-/deploymentstatus lezen",
      body: "Beschikbaar waar Argo geconfigureerd en bereikbaar is. Statuslezingen houden de deployment-poort verbonden met de live staat van de rollout.",
    },
    openGate: {
      name: "Deployment-goedkeuringspoort openen",
      body: "FixControl opent de poort als een vastgelegde, toewijsbare stap. De promotiebeslissing die volgt is altijd die van een mens.",
    },
    promote: {
      name: "Een gepauzeerde rollout promoten",
      body: "Loopt waar Argo geconfigureerd is, en alleen nadat de poort is goedgekeurd. De promote-aanroep wordt via de integratie-outbox verstuurd.",
    },
    rollback: {
      name: "Abort-/rollback-zichtbaarheid",
      body: "Getoond waar Argo is geconfigureerd om het te rapporteren, zodat abort- en rollback-status zichtbaar is terwijl de poort pending is.",
    },
    autonomous: {
      name: "Promotie zonder goedkeuring",
      body: "Bestaat hier niet. Een gepauzeerde rollout wordt gepromoot door een goedgekeurde poortbeslissing, of helemaal niet.",
    },
    singleGreen: {
      name: "Eén algemene ‘connected’-status",
      body: "Niet gebruikt. Status lezen en promoten rapporteren health apart, zodat een kapot promote-pad zich niet kan verschuilen achter een werkende statuslezing.",
    },
  },
  failures: {
    eyebrow: "Als een promotie faalt",
    title: "De poort houdt de rollout gepauzeerd tot iemand beslist",
    noteTitle: "Wat er na goedkeuring gebeurt",
    noteBody:
      "Zodra de poort is goedgekeurd, loopt de promote-actie via de integratie-outbox — opnieuw geprobeerd bij tijdelijke fouten, naar de dead-letter en gemarkeerd op de operationele tijdlijn als het blijft mislukken. De poortbeslissing zelf wordt naar het beslis-grootboek geschreven met actor, rol, kanaal, bewijs en consequentie.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen over de deployment-poort",
    q1: {
      q: "Deployt FixControl op eigen houtje naar productie?",
      a: "Nee. Promotie gebeurt alleen via een goedgekeurde deployment-poort. Tot een mens goedkeurt, blijft de rollout gepauzeerd.",
    },
    q2: {
      q: "Wat heeft FixControl nodig om een rollout te promoten?",
      a: "Argo geconfigureerd en bereikbaar voor de tenant — een expliciete opt-in — plus een goedgekeurde poort. Zonder de goedkeuring blijft de poort pending en de rollout blijft gepauzeerd.",
    },
    q3: {
      q: "Wat gebeurt er als een promotie mislukt?",
      a: "De promote-aanroep wordt opnieuw geprobeerd vanuit de outbox. Blijft het mislukken, dan gaat de job naar de dead-letter en legt de tijdlijn een mislukte promotie vast.",
    },
    q4: {
      q: "Kan FixControl automatisch terugrollen?",
      a: "Nee. FixControl brengt abort- en rollback-status naar voren waar Argo die rapporteert, maar terugrollen is de beslissing van je team. Er is geen geautomatiseerde rollback.",
    },
    q5: {
      q: "Worden deployment-promoties geaudit?",
      a: "Ja. Elke poortbeslissing staat in het beslis-grootboek, en de promotie die zij autoriseerde staat in de rollout-historie. De twee sluiten op elkaar aan.",
    },
    q6: {
      q: "Moet onze Argo of Kubernetes publiek bereikbaar zijn?",
      a: "Nee. Waar je cluster privé is, loopt de verbinding via de FC Agent: een service in je eigen omgeving die uitgaand met FixControl verbindt en de goedgekeurde promotie binnen je netwerk uitvoert. Er hoeft niets te worden blootgesteld.",
    },
  },
  related: {
    governance: {
      label: "Deployment-governance",
      description: "Bewijs erin, één vastgelegde beslissing, één promotie.",
    },
    ledger: {
      label: "Goedkeurings-grootboek",
      description: "Waar elke promotiebeslissing wordt bewaard.",
    },
    agent: {
      label: "FC Agent — private infrastructuur",
      description: "Uitsluitend uitgaande connectiviteit voor private clusters en CI.",
    },
  },
  cta: {
    title: "Zet een poort voor je volgende promotie",
    description:
      "Boek een demo en keur zelf een gepauzeerde rollout goed — waar Argo geconfigureerd is, wacht promotie op precies dat.",
    primary: "Boek een demo",
    secondary: "Vraag het FixControl",
  },
};
